import type { Emitter } from "@core/events/emitter";

export type ParticipantVec3 = readonly [number, number, number];
export type ParticipantQuat = readonly [number, number, number, number];

/**
 * Unity `ParticipantControllerVisibilityState`
 * (`SharedStateEventData.cs`) — serialized as the raw int wire field
 * `m_controllerVisibilityState`, meaningful on quest-controller keys only.
 */
export const PARTICIPANT_CONTROLLER_VISIBILITY = {
  Unknown: 0,
  Hidden: 1,
  Visible: 2,
} as const;

/**
 * Unity `MultiuserAppModel.QuestControllerSuffix`: quest controller
 * transforms are published under the user's app-instance id + `"-qc"`.
 * Their poses are relative and legitimately pass through the origin, so
 * they are exempt from the zero-position sentinel guard (AI-1188).
 */
export const QUEST_CONTROLLER_SUFFIX = "-qc";

export function isQuestControllerKey(key: string): boolean {
  return key.endsWith(QUEST_CONTROLLER_SUFFIX);
}

/**
 * Participant pose in **Unity world space** — same convention as Unity's
 * `ParticipantTransformData` wire shape (`p` / `r` / `tp` / `tv` / `tpv` /
 * `m_controllerVisibilityState`), so web and Unity clients exchange
 * transforms without translation at the model layer. Convert at the
 * renderer boundary via `runtime/assets/participantTransformConversion.ts`.
 */
export interface ParticipantTransform {
  readonly position: ParticipantVec3;
  readonly rotation: ParticipantQuat;
  readonly toolPosition?: ParticipantVec3 | null;
  readonly toolVisibility?: number;
  /** Unity `tpv` — tool panel visibility. */
  readonly toolPanelVisibility?: boolean;
  /** Unity `m_controllerVisibilityState` — see `PARTICIPANT_CONTROLLER_VISIBILITY`. */
  readonly controllerVisibilityState?: number;
}

export type MultiuserParticipantKind = "desktop" | "remote" | "tablet" | "quest" | "unknown";

/**
 * Unity `PresenterTools` ([Flags] int, wire field `at` / `activeTool`).
 * NOT the legacy `ActiveTool` enum in `UserInstanceBaseBehaviour` — the wire
 * always carries these flags.
 */
export const PRESENTER_TOOLS = {
  None: 0,
  LaserTool: 1,
  AnnotationTool: 2,
  FlashLightTool: 4,
  ExplosionTool: 8,
  SliceTool: 16,
  GrabTool: 32,
  RotateMoveTool: 64,
  ScaleTool: 128,
  TeleportationTool: 256,
  SketchTool: 512,
  ReposTool: 1024,
  BoundingBoxTool: 2048,
} as const;

export interface MultiuserParticipant {
  readonly key: string;
  readonly userId: string;
  readonly appInstanceId: string;
  readonly appName: string | null;
  readonly displayName: string;
  readonly initials: string;
  readonly email: string | null;
  /**
   * Unity `SessionParticipantData.ProjectId` (wire `project_id`) — the project
   * this participant currently has OPEN, or `null` when they are connected but
   * not in one (sitting on the projects grid). Unity's roster is account-wide,
   * so this field — NOT the shape of the roster — is what scopes a participant
   * to a project; read it through {@link IMultiuserSessionModel.getSessionParticipants}
   * rather than filtering by hand.
   *
   * `open_project` sets it, `close_project` clears it back to `null`
   * (`MultiuserAppModel.OnServerCloseProject`), and payloads that don't carry
   * it at all (heartbeats are a bare `BaseEventData`) leave it untouched.
   */
  readonly projectId: string | null;
  /**
   * Unity `SessionParticipantData.ProjectCustomerId` (wire `act_id`) — the
   * account that OWNS the open project, not the participant's own account.
   * Second term of `SessionParticipantInfo.IsInSession`; empty on either side
   * means "don't care", so it can only ever narrow a match, never widen one.
   */
  readonly projectCustomerId: string | null;
  readonly color: string;
  readonly isLocal: boolean;
  readonly isOnline: boolean;
  readonly kind: MultiuserParticipantKind;
  readonly hasHeadset: boolean;
  readonly lastSeenAt: number;
  readonly transform: ParticipantTransform | null;
  /**
   * Unity `SessionParticipantData.IsPresenter` (wire `is_presenter`) —
   * receivers follow ONLY a presenter's `load_scene` navigation.
   */
  readonly isPresenter: boolean;
  /**
   * Unity `SessionParticipantData.CanBePresenter` (wire `p`) — account-level
   * capability from the admin portal. Gates both the "Make Presenter" command
   * (`UserRolePermissions.CanAssignPresenter`) and self-election
   * (`MultiuserSessionModel.EnsurePresenter`). Web deviation: defaults to
   * TRUE when a payload never carried the field (Unity defaults false but
   * learns the real value from `load_project` full state, which the web
   * doesn't receive) — a false-negative here would let the web demote a
   * legitimate Unity presenter via spurious self-election.
   */
  readonly canBePresenter: boolean;
  /**
   * `SessionParticipantInfo.UserRole` — the peer's `SharedUserRole` INT from
   * the wire field `ur`. Retained so a roster surface can show what a peer may
   * do; nothing gates on a PEER's role locally (Unity doesn't either).
   */
  readonly userRole?: number;
  /** Unity `SessionParticipantData.CurrentSceneId` (wire `sid`). */
  readonly currentSceneId: string | null;
  /**
   * Unity `SessionParticipantData.ActiveTool` (wire `at`, `PresenterTools`
   * flags int) — updated by shared-state rows and live `active_tool`
   * (tc 0) events. Drives which tool visual the presence renderer shows.
   */
  readonly activeTool: number;
}

/**
 * Which avatar prefab Unity spawns for a peer — the decision table in
 * `MultiuserSessionModel.CreateNetworkUser` (`:524`), in one place because
 * two very different surfaces need it: what the peer LOOKS like, and whether
 * their pose is tracked (see {@link participantPoseIsTracked}).
 *
 * | participant             | Unity call       | behaviour          |
 * | ----------------------- | ---------------- | ------------------ |
 * | desktop + `HasHeadset`  | `CreateHead`     | `HeadsetBehaviour` |
 * | desktop, no headset     | `CreateDisplay`  | `DisplayBehaviour` |
 * | iPad                    | `CreateTablet`   | `TabletBehaviour`  |
 * | Campfire Remote         | `CreateRemote`   | `RemoteBehaviour`  |
 * | Quest / Quest Link      | `CreateQuest`    | `QuestBehaviour`   |
 *
 * `unknown` follows the desktop row rather than Unity's "spawn nothing":
 * the web prefers visible presence, and an app name is genuinely absent on
 * the payloads that carry no roster fields.
 */
export type ParticipantAvatarPrefab = "display" | "head" | "tablet" | "remote" | "quest";

export type ParticipantDeviceShape = Pick<MultiuserParticipant, "kind" | "hasHeadset">;

export function participantAvatarPrefab(participant: ParticipantDeviceShape): ParticipantAvatarPrefab {
  switch (participant.kind) {
    case "quest":
      return "quest";
    case "remote":
      return "remote";
    case "tablet":
      return "tablet";
    case "desktop":
    case "unknown":
    default:
      return participant.hasHeadset ? "head" : "display";
  }
}

/**
 * Can this peer's avatar ever report LOST tracking — i.e. does Unity's
 * `UserInstanceBaseBehaviour.IsPosLive` mean anything for it?
 *
 * Only the tracked-device prefabs start `CoUpdatePosLiveState`
 * (`HeadsetBehaviour:131`, `QuestBehaviour:147`, `RemoteBehaviour:18`), and
 * that coroutine is the ONLY thing that ever clears the flag. `DisplayBehaviour`
 * and `TabletBehaviour` never start it, so a screen peer's `IsPosLive` keeps
 * its `true` default (`UserInstanceBaseBehaviour:175`) for the object's whole
 * life — which is why Unity lets you follow a desktop peer unconditionally.
 */
export function participantPoseIsTracked(participant: ParticipantDeviceShape): boolean {
  const prefab = participantAvatarPrefab(participant);
  return prefab === "head" || prefab === "quest" || prefab === "remote";
}

/** Unity `FollowedChangedEventArgs` (`MultiuserAppModel.cs:227`). */
export interface FollowChange {
  readonly followerKey: string;
  readonly oldFollowedKey: string;
  readonly newFollowedKey: string;
}

/** Unity `ValueChangedArgs<string>` payload of `PresenterChanged`. */
export interface PresenterChange {
  readonly oldPresenterKey: string | null;
  readonly newPresenterKey: string | null;
}

/**
 * Arguments of Unity `MultiuserAppModel.GetSessionParticipants(projectId,
 * accountId, excludeRemote, excludeOffline)` (`MultiuserAppModel.cs:580`).
 */
export interface SessionParticipantsQuery {
  /**
   * The project to scope to. `null` yields an EMPTY list — Unity's
   * `ParticipantsListViewModel.RefreshParticipantsList` returns early when
   * `CurrentProject == null`, and matching every not-in-a-project participant
   * against a null project is exactly the bug this query exists to prevent.
   */
  readonly projectId: string | null;
  /** Owning account of `projectId`. Omit/empty to skip the account term. */
  readonly accountId?: string | null;
  /**
   * Unity's `excludeRemote`, which **defaults to TRUE** on the C# overload.
   * The roster LIST passes it (Campfire Remote is a phone companion, not a
   * seat in the room); the scene renderer passes `false`, because a remote
   * peer still gets a phone-frame avatar (`MultiuserSessionModel.cs:349`).
   */
  readonly excludeRemote?: boolean;
  /** Drop participants gone dark past the heartbeat timeout. Defaults TRUE. */
  readonly excludeOffline?: boolean;
}

/**
 * Port of `SessionParticipantInfo.IsInSession(sessionId, accountId)`
 * (`MultiuserAppModel-Info.cs:167`) — verbatim, including both empty-guards on
 * the account term, which make it fail OPEN: a peer that never sent `act_id`
 * is matched on project alone rather than hidden.
 *
 * The project comparison is exact. A participant with no open project
 * (`null`) therefore matches NO project — that is what keeps everyone sitting
 * on the projects grid out of an open project's roster and scene.
 */
export function isParticipantInSession(
  participant: MultiuserParticipant,
  projectId: string | null,
  accountId?: string | null,
): boolean {
  if (!projectId) return false;
  if (participant.projectId !== projectId) return false;
  if (!accountId || !participant.projectCustomerId) return true;
  return participant.projectCustomerId === accountId;
}

export interface IMultiuserSessionModel {
  readonly onParticipantsChanged: Emitter<ReadonlyArray<MultiuserParticipant>>;
  readonly onParticipantTransformChanged: Emitter<MultiuserParticipant>;
  /**
   * A non-local participant JOINED the active project (`open_project` moved
   * them into it, or heartbeat flipped its existing row offline→online) —
   * mirrors Unity `MultiuserAppModel.ParticipantJoinSession` narrowed by
   * `MultiuserSessionModel.OnAppModelParticipantJoinSession`. A plain
   * `launch_application`/shared-state insert, or a join in another project,
   * is not this session's join. Drives the presenter's grab `sync:true`
   * re-broadcast.
   */
  readonly onParticipantJoined: Emitter<MultiuserParticipant>;
  /** Port of `MultiuserAppModel.PresenterChanged` (old/new may be null). */
  readonly onPresenterChanged: Emitter<PresenterChange>;
  /** Port of `MultiuserAppModel.FollowedParticipantChanged` (any follower). */
  readonly onFollowChanged: Emitter<FollowChange>;
  /**
   * The LOCAL user's follow target changed (Unity `ILocalUserModel.FollowTarget`
   * + `FollowStatusChanged`). `null` = not following. Camera + overlay bind here.
   */
  readonly onLocalFollowTargetChanged: Emitter<MultiuserParticipant | null>;
  /**
   * An `ask_to_follow` (tc 5) invitation arrived for the local user — the
   * payload is the ASKER; the UI shows Unity's follow/decline toast
   * ("{name} is asking you to follow their view").
   */
  readonly onAskedToFollow: Emitter<MultiuserParticipant>;

  /**
   * The GLOBAL roster — everyone this client knows to be connected, whatever
   * project they are in, minus offline rows and quest-controller transform
   * carriers. Unity's `m_allParticipants` is account-wide for the same reason:
   * the event relay is scoped to the ACCOUNT, not the project, so a client
   * legitimately hears from users in projects it is not in.
   *
   * This is deliberately NOT scoped to the open project, because the projects
   * grid needs it that way — each card shows who is in THAT project
   * (`ProjectParticipantsListView`, Unity `ProjectParticipantsListViewModel`).
   *
   * Any surface that means "who is in a project" — the header roster, the
   * in-scene avatars — must go through {@link getSessionParticipants} instead.
   */
  readonly participants: ReadonlyArray<MultiuserParticipant>;
  /**
   * Port of `MultiuserAppModel.GetSessionParticipants` — the global roster
   * filtered to one project (and optionally its owning account), which is how
   * every in-project surface in Unity reads presence.
   *
   * Use this when the project is one the caller names — the projects grid
   * asking "who is in THAT card's project". For the project that is currently
   * open, prefer {@link getActiveProjectParticipants}, which resolves the
   * owning account for you.
   */
  getSessionParticipants(query: SessionParticipantsQuery): ReadonlyArray<MultiuserParticipant>;
  /**
   * Everyone in the project the local user has open, with its owning account
   * resolved — the web equivalent of Unity's `MultiuserSessionModel` holding
   * `m_projectID` + `m_accountId` and passing both to every roster read.
   *
   * Returns EMPTY when no project is open, which is the point: it is what
   * keeps a client sitting on the projects grid from treating every connected
   * user as a peer.
   */
  getActiveProjectParticipants(
    options?: Pick<SessionParticipantsQuery, "excludeRemote" | "excludeOffline">,
  ): ReadonlyArray<MultiuserParticipant>;
  readonly localParticipantKey: string | null;
  /** Key of the participant the LOCAL user is following, or null. */
  readonly localFollowedKey: string | null;

  start(): void;
  stop(): void;
  /**
   * Feed the local pose — call EVERY FRAME like Unity
   * `DesktopLocalUserModel.LateUpdate`; the model owns the whole cadence
   * (1 cm / 5° change gate, 50 Hz wire cap with per-frame retry, laser
   * show/hide bypass). Callers must NOT add their own throttles. Fields left
   * absent inherit the last local transform (pose-only samplers don't stomp
   * the presenter-tools publisher's `tp`/`tv`).
   */
  updateLocalTransform(transform: ParticipantTransform): void;
  /**
   * Publish the local user's active presenter tool (`active_tool`, tc 0 —
   * Unity `SendChangeActiveToolEvent`): sent once per tool CHANGE, with the
   * current laser target/visibility stamped; the continuous laser point
   * keeps flowing via `participant_transform` `tp`/`tv`. Also feeds the
   * heartbeat/presence `at` field.
   */
  setLocalActiveTool(activeTool: number): void;

  /**
   * Whether the local user is currently in an immersive WebXR session.
   * Stamped as `hd` / `HasHeadset` on presence payloads so peers render the
   * mannequin-head representation instead of the desktop device frame
   * (Unity `CreateNetworkUser`: desktop + HasHeadset → head). Publishes one
   * immediate presence event on a real change, like `setLocalActiveTool`.
   */
  setLocalHasHeadset(hasHeadset: boolean): void;

  /** Current presenter in the active project, if known. */
  getPresenter(): MultiuserParticipant | null;
  /** Who `key` is following (from the shared follow graph), or null. */
  getFollowedKeyOf(key: string): string | null;
  /** Unity `MultiuserAppModel.IsUserFollowing` — is `key` following anyone? */
  isUserFollowing(key: string): boolean;
  /** Unity `MultiuserAppModel.CanFollow` — circular-follow guard for the local user. */
  canFollow(key: string): boolean;
  /**
   * Unity `NetworkUsersModel.GetIsUserPosLive` (`:522`): is this peer's avatar
   * reporting a live pose? Screen peers always are — see
   * {@link participantPoseIsTracked}; tracked devices need a real,
   * non-sentinel pose.
   */
  isParticipantTracking(key: string): boolean;

  /**
   * Unity `MultiuserAppModel.MakePresenterAsync`: broadcast `presenter_change`
   * nominating `key`. Fire-and-forget — state flips when the echo returns.
   */
  makePresenter(key: string): void;
  /**
   * Start following `key` locally (camera + overlay) and broadcast
   * `follower_change`. Returns false when the guard rails reject it
   * (unknown/offline/self/not-tracking/circular).
   */
  followUser(key: string): boolean;
  /** Stop following: restore state and broadcast an empty `follower_change`. */
  stopFollowing(): void;
}

export const MULTIUSER_SESSION_MODEL = Symbol.for("Campfire.MultiuserSessionModel");
